// src/pages/Home/components/HomeHeroSlider.jsx

import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import { Autoplay, EffectFade, Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import heroSlides from "./../data/heroSlides";
import AnimatedApplicationName from "./AnimatedApplicationName";

import "swiper/css";
import "swiper/css/effect-fade";

const cx = (...classes) => classes.filter(Boolean).join(" ");

function HeroLink({ action, variant = "primary" }) {
  if (!action) return null;

  const className = cx(
    "group inline-flex min-h-12 items-center justify-center gap-3 rounded-full px-6 py-3",
    "text-sm font-bold transition duration-300",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2",
    "focus-visible:ring-offset-slate-950",
    variant === "primary"
      ? [
          "bg-[#ff7900] text-white",
          "shadow-[0_16px_40px_rgba(255,121,0,0.28)]",
          "hover:-translate-y-0.5 hover:bg-[#ff8a1f]",
          "hover:shadow-[0_20px_46px_rgba(255,121,0,0.38)]",
        ].join(" ")
      : [
          "border border-white/35 bg-white/10 text-white",
          "shadow-[0_12px_32px_rgba(0,0,0,0.12)]",
          "backdrop-blur-md",
          "hover:-translate-y-0.5 hover:border-[#1677ff]",
          "hover:bg-[#1677ff] hover:text-white",
        ].join(" "),
  );

  const content = (
    <>
      <span>{action.label}</span>

      <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );

  if (action.to?.startsWith("#")) {
    return (
      <a href={action.to} className={className}>
        {content}
      </a>
    );
  }

  if (/^https?:\/\//i.test(action.to || "")) {
    return (
      <a
        href={action.to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={action.to} className={className}>
      {content}
    </Link>
  );
}

function SliderControls({ swiper, activeIndex }) {
  return (
    <div className="flex items-center gap-4">
      <div className="hidden items-center gap-2 sm:flex">
        {heroSlides.map((slide, index) => {
          const isActive = activeIndex === index;

          return (
            <button
              key={slide.id}
              type="button"
              onClick={() => swiper?.slideToLoop(index)}
              className={cx(
                "relative h-1 overflow-hidden rounded-full transition-all duration-300",
                isActive
                  ? "w-11 bg-white/30"
                  : "w-5 bg-white/25 hover:bg-white/50",
              )}
              aria-label={`Afficher la diapositive ${index + 1}`}
              aria-current={isActive ? "true" : undefined}
            >
              {isActive ? (
                <span className="absolute inset-y-0 left-0 w-full origin-left animate-[heroProgress_6.5s_linear_forwards] rounded-full bg-gradient-to-r from-[#1677ff] to-[#ff7900]" />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => swiper?.slidePrev()}
          className={cx(
            "grid h-11 w-11 place-items-center rounded-full",
            "border border-white/30 bg-slate-950/20 text-white",
            "backdrop-blur-md transition duration-300",
            "hover:-translate-y-0.5 hover:border-white",
            "hover:bg-white hover:text-slate-950",
          )}
          aria-label="Diapositive précédente"
        >
          <FaChevronLeft className="text-xs" />
        </button>

        <button
          type="button"
          onClick={() => swiper?.slideNext()}
          className={cx(
            "grid h-11 w-11 place-items-center rounded-full",
            "border border-white/30 bg-slate-950/20 text-white",
            "backdrop-blur-md transition duration-300",
            "hover:-translate-y-0.5 hover:border-white",
            "hover:bg-white hover:text-slate-950",
          )}
          aria-label="Diapositive suivante"
        >
          <FaChevronRight className="text-xs" />
        </button>
      </div>
    </div>
  );
}

function HeroBrandBadge() {
  return (
    <div className="inline-flex max-w-full items-center gap-3 rounded-full border border-white/15 bg-slate-950/20 px-4 py-2.5 text-white shadow-lg backdrop-blur-md">
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#ff7900] opacity-50" />

        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#ff7900]" />
      </span>

      <span className="truncate text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 sm:text-xs">
        Des outils conçus pour les clubs & collectivités
      </span>
    </div>
  );
}

export default function HomeHeroSlider() {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section
      className="relative overflow-hidden bg-[#071426]"
      aria-label="Présentation de MonAppliClub"
    >
      <Swiper
        modules={[Autoplay, EffectFade, Keyboard]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        loop
        speed={1000}
        keyboard={{ enabled: true }}
        autoplay={{
          delay: 6500,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
        }}
        className="h-[100svh] min-h-[700px] max-h-[1000px]"
      >
        {heroSlides.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <article className="relative h-full min-h-[700px] overflow-hidden">
              <img
                src={slide.image}
                alt=""
                loading={index === 0 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : "auto"}
                className="absolute inset-0 h-full w-full scale-[1.025] object-cover"
                style={{
                  objectPosition: slide.imagePosition,
                }}
              />

              {/* Assombrissement général */}
              <div className="absolute inset-0 bg-[#071426]/35" />

              {/* Dégradé principal de lecture */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#071426]/95 via-[#071426]/72 to-[#071426]/10" />

              {/* Dégradé inférieur */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#071426]/85 via-transparent to-[#071426]/20" />

              {/* Halo bleu de la marque */}
              <div className="pointer-events-none absolute -left-32 top-1/3 h-[440px] w-[440px] rounded-full bg-[#1677ff]/15 blur-[110px]" />

              {/* Halo orange de la marque */}
              <div className="pointer-events-none absolute bottom-0 left-1/3 h-[320px] w-[320px] rounded-full bg-[#ff7900]/10 blur-[100px]" />

              <div
                className={cx(
                  "relative z-10 mx-auto flex h-full max-w-[1480px] flex-col justify-end",
                  "px-4 pb-28 pt-28",
                  "sm:px-6 sm:pb-32",
                  "lg:px-8 lg:pb-36",
                )}
              >
                <div className="max-w-[1120px]">
                  <HeroBrandBadge />

                  

                  <div className="mt-5 sm:mt-6">
                    <AnimatedApplicationName
                      interval={2700}
                      showLogo
                    />
                  </div>

                  <div className="mt-6 flex items-center gap-3">
                    <span className="h-[3px] w-8 rounded-full bg-[#1677ff] sm:w-11" />

                    <span className="h-[3px] w-4 rounded-full bg-[#ff7900] sm:w-6" />

                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/75 sm:text-xs lg:text-sm">
                      {slide.eyebrow}
                    </p>
                  </div>

                  <h1 className="mt-5 max-w-4xl">
                    <span className="block text-xl font-semibold leading-tight text-white sm:text-3xl lg:text-[2.6rem]">
                      {slide.title}
                    </span>

                    {slide.highlightedTitle ? (
                      <span
                        className={cx(
                          "mt-2 block",
                          "text-xl font-bold leading-tight sm:text-3xl lg:text-[2.6rem]",
                          "text-transparent",
                          "bg-gradient-to-r from-[#58a6ff] via-white to-[#ff9a3d]",
                          "bg-clip-text",
                        )}
                      >
                        {slide.highlightedTitle}
                      </span>
                    ) : null}
                  </h1>

                  <p className="mt-5 max-w-2xl text-sm leading-7 text-white/75 sm:text-base sm:leading-8 lg:text-lg">
                    {slide.description}
                  </p>

                  <div className="mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:items-center">
                    <HeroLink action={slide.primaryAction} />

                    <HeroLink
                      action={slide.secondaryAction}
                      variant="secondary"
                    />
                  </div>
                </div>
              </div>

              <div className="absolute inset-x-0 bottom-0 z-20">
                <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
                  <div
                    className={cx(
                      "flex min-h-20 items-center justify-between gap-5",
                      "border-t border-white/15",
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
                        MonAppli
                        <span className="text-[#ff7900]">Club</span>
                      </p>

                      <p className="mt-1 hidden truncate text-sm text-white/80 sm:block">
                        {slide.note}
                      </p>
                    </div>

                    <SliderControls
                      swiper={swiperRef.current}
                      activeIndex={activeIndex}
                    />
                  </div>
                </div>
              </div>
            </article>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}